/**
 * Production Environment Configuration Entry Point
 * 
 * This file ensures only production environment config is bundled
 * when building for production deployment.
 */

import type { Environment, EnvironmentConfig } from './environments';
import {
  PRICING_CONFIG,
  PREPAID_PLAN_IDS,
  RECURRING_PLAN_IDS,
  FREE_PLAN_IDS,
  ALL_PLAN_IDS,
  STRIPE_PRICE_TYPES,
  getFreePlan,
  getRecurringPlan,
  getPrepaidPlan,
  getAllRecurringPlans,
  getAllPrepaidPlans,
  getRecommendedRecurringPlan,
  getRecommendedPrepaidPlan,
  formatPrice,
  getPrepaidDurationInSeconds,
  stripeToPrepaidPlan,
} from './pricing';
import {
  getFeaturesForTier,
  getFeaturesForTierAndEnvironment,
  isFeatureEnabled,
  getStorageLimit,
  getDynamicFeatures,
  isDynamicFeatureEnabled,
  RATE_LIMITS,
} from './features';
import {
  getInfrastructureConfig,
  getAWSConfig,
  getDomainConfig,
  getStackName,
  getS3BucketName,
  getDynamoTableName,
  getLambdaFunctionName,
  getSecretName,
} from './infrastructure';
import {
  getSecurityConfig,
  getCryptoConfig,
  getJWTConfig,
  getPasswordConfig,
  getSessionConfig,
  getSecurityHeaders,
  validatePassword,
  getSessionTimeout,
  SECURITY_CONSTANTS,
} from './security';
import {
  PAYMENT_CONFIG,
  getDisplayPaymentMethods,
  getEnabledPaymentMethods,
  getAllPaymentMethods,
  getPaymentMethod,
  isPaymentMethodEnabled,
  getComingSoonPaymentMethods,
} from './payments';
import {
  getNotificationConfig,
  getSlackConfig,
  isSlackEnabled,
  SLACK_EVENT_TYPES,
  SLACK_MESSAGE_COLORS,
} from './notifications';

// Only include prod environment configuration
const ENVIRONMENT: Environment = 'prod';

const environmentConfig: EnvironmentConfig = {
  environment: 'prod',
  displayName: 'Safermark',
  apiUrl: 'https://api.safermark.com/api',
  appUrl: 'https://safermark.com',
  extensionDownloadUrl: 'https://chromewebstore.google.com/detail/safermark-secure-private/hdfokkmjfonpciemfincmpnaghdilaff',
  notificationEmail: 'thi.senaratne@gmail.com',
  contactEmail: 'admin@bitdirection.com',
  stripePublishableKey: 'pk_live_51RvsQY2MMVxrzmVLgQ84m79o41Rknczva29Rmu9OiUV0xX1FuQYGXBq9pSKl5zTB9lOGjLAOUHHmlNZTkKgr4F8u00vmLi97TN',
  
  api: {
    corsOrigins: [
      'https://safermark.com',
      'https://www.safermark.com',
      'chrome-extension://prod-extension-id'
    ],
    s3BucketName: 'safermark-vaults-prod-563687506272',
    awsRegion: 'us-east-1',
    dynamoTableName: 'safermark-users-prod'
  },
  
  extension: {
    extensionId: 'prod-extension-id',
    allowDevTools: false,
    showEnvironmentIndicator: false
  },

  analytics: {
    googleAnalyticsId: 'G-NLN3PC3X5B', // Replace with actual prod GA ID
    enabled: true
  },

  development: {
    enableDebugLogs: false,
    enableHotReload: false,
    mockStripePayments: false
  }
};

/**
 * Get current environment (always returns 'prod' for this build)
 */
export function getCurrentEnvironment(): Environment {
  return ENVIRONMENT;
}

/**
 * Get configuration for current environment
 */
export function getEnvironmentConfig(): EnvironmentConfig {
  return environmentConfig;
}

/**
 * Check if current environment is development-like
 */
export function isDevelopmentEnvironment(): boolean {
  return false;
}

/**
 * Check if current environment is production
 */
export function isProductionEnvironment(): boolean {
  return true;
}

/**
 * Legacy compatibility - map old extension environment names to new ones
 */
export function mapLegacyEnvironment(legacyEnv: 'localhost' | 'development' | 'production'): Environment {
  const mapping = {
    'localhost': 'local' as const,
    'development': 'dev' as const,
    'production': 'prod' as const
  };
  return mapping[legacyEnv];
}

/**
 * Main configuration object providing easy access to all configs
 */
export const config = {
  // Current environment detection
  get env() {
    return ENVIRONMENT;
  },
  
  get isDev() {
    return false;
  },
  
  get isProd() {
    return true;
  },
  
  // Environment configuration
  get current() {
    return environmentConfig;
  },
  
  get environment() {
    return environmentConfig;
  },
  
  // Quick access to common values
  get apiUrl() {
    return environmentConfig.apiUrl;
  },
  
  get appUrl() {
    return environmentConfig.appUrl;
  },
  
  get displayName() {
    return environmentConfig.displayName;
  },
  
  get stripePublishableKey() {
    return environmentConfig.stripePublishableKey;
  },
  
  get corsOrigins() {
    return environmentConfig.api.corsOrigins;
  },
  
  get api() {
    return environmentConfig.api;
  },
  
  // AWS configuration shortcuts
  get aws() {
    return getAWSConfig(ENVIRONMENT);
  },
  
  get s3BucketName() {
    return getS3BucketName(ENVIRONMENT);
  },
  
  get dynamoTableName() {
    return getDynamoTableName(ENVIRONMENT);
  },
  
  get secretName() {
    return getSecretName(ENVIRONMENT);
  },
  
  // Security configuration shortcuts
  get security() {
    return getSecurityConfig(ENVIRONMENT);
  },
  
  get crypto() {
    return getCryptoConfig(ENVIRONMENT);
  },
  
  get jwt() {
    return getJWTConfig(ENVIRONMENT);
  },
  
  // Pricing shortcuts
  get pricing() {
    return PRICING_CONFIG;
  },
  
  // Rate limits
  get rateLimits() {
    return RATE_LIMITS;
  },
  
  // Payment methods
  get payments() {
    return PAYMENT_CONFIG;
  },
  
  get paymentMethods() {
    return getDisplayPaymentMethods();
  },
  
  // Security constants
  get securityConstants() {
    return SECURITY_CONSTANTS;
  },
};

// Export all the helper functions and constants
export {
  // Types
  type Environment,
  type EnvironmentConfig,
  
  // Pricing
  PRICING_CONFIG,
  PREPAID_PLAN_IDS,
  RECURRING_PLAN_IDS,
  FREE_PLAN_IDS,
  ALL_PLAN_IDS,
  STRIPE_PRICE_TYPES,
  getFreePlan,
  getRecurringPlan,
  getPrepaidPlan,
  getAllRecurringPlans,
  getAllPrepaidPlans,
  getRecommendedRecurringPlan,
  getRecommendedPrepaidPlan,
  formatPrice,
  getPrepaidDurationInSeconds,
  stripeToPrepaidPlan,
  
  // Features
  getFeaturesForTier,
  getFeaturesForTierAndEnvironment,
  isFeatureEnabled,
  getStorageLimit,
  getDynamicFeatures,
  isDynamicFeatureEnabled,
  RATE_LIMITS,
  
  // Infrastructure
  getInfrastructureConfig,
  getAWSConfig,
  getDomainConfig,
  getStackName,
  getS3BucketName,
  getDynamoTableName,
  getLambdaFunctionName,
  getSecretName,
  
  // Security
  getSecurityConfig,
  getCryptoConfig,
  getJWTConfig,
  getPasswordConfig,
  getSessionConfig,
  getSecurityHeaders,
  validatePassword,
  getSessionTimeout,
  SECURITY_CONSTANTS,
  
  // Payments
  PAYMENT_CONFIG,
  getDisplayPaymentMethods,
  getEnabledPaymentMethods,
  getAllPaymentMethods,
  getPaymentMethod,
  isPaymentMethodEnabled,
  getComingSoonPaymentMethods,

  // Notifications
  getNotificationConfig,
  getSlackConfig,
  isSlackEnabled,
  SLACK_EVENT_TYPES,
  SLACK_MESSAGE_COLORS,
};

// Default export
export default config;